<template>
  <div class="schedule-builder">
    <!-- Timezone reference (shop for now; contact = phase 2) -->
    <div class="schedule-builder__tz">
      <i class="far fa-globe" />
      <span>
        <strong>{{ t('automatedScenarios.globalSettings.schedule.timezone_label') }}</strong>
        {{ shopTimezone }}
      </span>
    </div>

    <label class="schedule-builder__section-label">
      {{ t('automatedScenarios.globalSettings.schedule.rules_label') }}
    </label>

    <div v-if="rules.length" class="schedule-builder__rules">
      <ScheduleRuleEditor
        v-for="(rule, index) in rules"
        :key="rule.id"
        :rule="rule"
        @update="onRuleUpdate(index, $event)"
        @remove="onRuleRemove(index)"
      />
    </div>
    <div v-else class="schedule-builder__empty">
      {{ t('automatedScenarios.globalSettings.schedule.no_rules') }}
    </div>

    <button
      type="button"
      class="schedule-builder__add"
      @click="onAddRule"
    >
      <i class="far fa-plus" />
      {{ t('automatedScenarios.globalSettings.schedule.add_rule') }}
    </button>

    <p class="schedule-builder__note">
      {{ t('automatedScenarios.globalSettings.schedule.footer_note') }}
    </p>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { usePrimeVue } from 'primevue/config';
import { UserState } from '@/composables/User';
import type { FlowScheduleConfig, FlowScheduleRule } from '@/types/flow-settings';
import ScheduleRuleEditor from './ScheduleRuleEditor.vue';

let ruleSeq = 0;
const newRuleId = (): string => {
  ruleSeq += 1;
  return `rule-${Date.now().toString(36)}-${ruleSeq}`;
};

export default defineComponent({
  name: 'ScheduleBuilder',

  components: { ScheduleRuleEditor },

  props: {
    modelValue: {
      type: Object as PropType<FlowScheduleConfig>,
      required: true,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const primevue = usePrimeVue();

    // Localise the PrimeVue Calendar (the app configures it per-usage, not
    // globally). Only French needs overriding; English is PrimeVue's default.
    onMounted(() => {
      if (locale.value === 'fr') {
        primevue.config.locale.dayNamesMin = ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'];
        primevue.config.locale.monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
        primevue.config.locale.monthNamesShort = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
        primevue.config.locale.today = 'Aujourd\'hui';
        primevue.config.locale.clear = 'Effacer';
        primevue.config.locale.firstDayOfWeek = 1;
      }
    });

    // Evaluation timezone = the shop's IANA zone (e.g. "Europe/Paris").
    const shopTimezone = computed(() => UserState.activeShop?.timezone || 'Europe/Paris');

    const rules = computed(() => props.modelValue.rules ?? []);

    const emitRules = (nextRules: FlowScheduleRule[]) => {
      emit('update:modelValue', { ...props.modelValue, rules: nextRules });
    };

    const onRuleUpdate = (index: number, rule: FlowScheduleRule) => {
      const next = [...rules.value];
      next[index] = rule;
      emitRules(next);
    };

    const onRuleRemove = (index: number) => {
      const next = [...rules.value];
      next.splice(index, 1);
      emitRules(next);
    };

    const onAddRule = () => {
      emitRules([...rules.value, { id: newRuleId(), effect: 'include' }]);
    };

    return {
      t,
      shopTimezone,
      rules,
      onRuleUpdate,
      onRuleRemove,
      onAddRule,
    };
  },
});
</script>

<style lang="scss" scoped>
.schedule-builder {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__tz {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #eef4fb;
    border: 1px solid #d6e4f3;
    border-radius: 9px;
    padding: 10px 14px;
    font-size: 0.8rem;
    color: #3c5a7a;

    i { color: #4a90d9; }
  }

  &__section-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #2f3947;
  }

  &__rules {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__empty {
    font-size: 0.8rem;
    color: #8a97a8;
    padding: 10px 0;
  }

  &__add {
    width: 100%;
    border: 1.5px dashed #b9d3ee;
    background: #f3f8fe;
    color: #4a90d9;
    border-radius: 10px;
    padding: 12px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:hover { background: #e8f2fd; }
  }

  &__note {
    font-size: 0.72rem;
    color: #8a97a8;
    margin: 0;
    line-height: 1.5;
  }
}
</style>
